<template>
<div class="chapter">
<div class="inner_page">
<div class="chapter1" id="pgepubid00004">
<h2 class="c4">PROOFS AND TRIALS OF GUILT IN SUPERSTITIOUS AGES.</h2>
</div>
<p class="c10">It were well, perhaps, did the cruelties practised in
former ages lay generally at the door of superstition.
The extraordinary trials to which those suspected
of any guilty action were conducted with many
devout ceremonies, by the ministers of religion,
were declared to be the judgments of God. The
kinds of ordeal were various, <em class="calibre12">e. g.</em> holding in the
hand a red hot bar; plunging the arm into boiling
water; walking blindfold amidst burning ploughshares;
passing through fires; challenging the
accuser to single combat, when frequently the ablest
champion was permitted to supply his place; swallowing
a morsel of consecrated bread; swimming
or sinking in a river for witchcraft, or, as it was
called, weighing a witch; stretching out the arms
before the cross, till the soonest wearied dropped
his arms, and lost his estate, which was decided by
this very short process, called <i class="calibre12"><span xml:lang="la">juidcium crucis</span></i>, &amp;c.</p>
<p class="c11">A dispute occurred between the Bishop of Paris
and the Abbot of St. Denis, about the patronage of
a monastery, and Pepin, surnamed the Short, not
being able to pronounce upon their confused claims,
<span id="Page_10" title="10"></span>decreed that it should be settled by one of these
judgments of God: viz. <em class="calibre12">The judgment of the
cross.</em> Each of the disputants chose a man, and
both of the men appeared in the chapel, where they
extended their arms in the form of a cross. The
spectators, more orderly than those of the present
day; still, although they watched every motion of
the combatants with the most pious attention, the
old English spirit, which rules so prevalently at the
present period, was proof against every other consideration—they
betted on the feat, first on one
side, then on the other, according as the odds seemed
to run in favour or against. The Bishop’s man
was first tried; he let his arms drop and ruined his
patron for ever. Though these trials might sometimes
be evaded by the artifice of the priest, numerous,
nevertheless, were the innocent victims
who suffered from these superstitious practices.</p>
<p class="c11">They were very frequent between the tenth and
twelfth century. William Rufus, having accused
Hildebert, the Bishop of Mans, of high treason,
was on the verge of submitting to one of these
trials, when he was convinced by Ives, Bishop of
Chartres, that they were against the canons of the
constitution of the church, and adds, that in this
manner “<i class="calibre12"><span xml:lang="la">Innocentiam defendere, est innocentiam
perdere</span></i>.” In 1066 an abbot of St. Aubin of Angers,
having refused to present a horse to the viscount
of Tours, which the viscount claimed in right
of his lordship, whenever an abbot first took possession
of that abbey; the ecclesiastic offered to
justify himself by the trial of the ordeal, or by
duel, for which purpose he proposed to find a substitute.
<span id="Page_11" title="11"></span>The duel was first agreed to by the viscount;
but, reflecting that these combatants, though
sanctioned by the church, depended solely on the
address or vigour of the adversary, and consequently
could afford no substantial proof of the
equity of his claim, he proposed to compromise the
matter in a manner which strongly characterised
these times: he surrendered his claim, on condition
that the abbot should not forget to mention him,
his wife, and his brothers, in his prayers! As the
orisons appeared to the abbot of comparatively
little value with the horse, the proposal was accepted.</p>
<p class="c11">In the tenth century the right of representation
was not settled: it was a question whether a son’s
sons ought to be accounted among the children of
the family, and succeed equally with their uncles,
if their fathers happened to die while their grandfathers
survived. This point was decided by one
of these combats. The champion in behalf of
the right of children to represent their deceased
father, proved victorious. It was then established
by a perpetual decree, that they should from that
time forward share in the inheritance along with
their uncles.</p>
<p class="c11">In the eleventh century, the same mode was
adopted, to decide between two rival liturgies! A
couple of knights, clad in complete armour, were
the tests to decide which was the true and authentic
liturgy.</p>
<p class="c11">The capitularies of Dagobert say, that if two
neighbours dispute respecting the boundaries of
their possessions, let a piece of turf of the contested
<span id="Page_12" title="12"></span>land be dug up by the judge, and brought
by him into the court, and the two parties shall
touch it with the points of their swords, calling on
God to witness their claims: after this, let them
combat, and let victory prove who is right or who
is wrong. In these combats in Germany, a solemn
circumstance was practised in these judicial combats.
In the midst of the lists they placed a <em class="calibre12">bier</em>;
by the side of which stood the accuser and the
accused, one at the head and the other at the foot,
where they leaned in profound silence for some
time before the combat commenced. In his preface
to Way’s Fableaux, Mr. Ellis shews how
faithfully the manners of the age are painted in
these ancient tales, by observing the judicial
combat introduced by a writer of the 14th century,
who, in his poem, represents Pilate as
challenging Jesus Christ to <em class="calibre12">single combat</em>; and
another, who describes the person who pierced
the side of Christ as a knight who jousted with
Jesus.</p>
<p class="c11">It appears that judicial combat was practised
by the Jews. Whenever the Rabbins had to decide
on a dispute about property between two parties,
neither of which could produce evidence to
substantiate the claim, it was terminated by single
combat. The Rabbins were impressed with a
notion that consciousness of right would give additional
confidence and strength to the rightful
possessor. It may, however, be more philosophical
to observe, that such judicial combats were more
frequently favourable to the criminal than to the
innocent, because the bold wicked man is usually
<span id="Page_13" title="13"></span>more ferocious and hardy than he whom he singles
out as his victim, and who only wishes to preserve
his own quiet enjoyments: in this case the assailant
is the most terrific opponent.</p>
<p class="c11">Those who were accused of robbery in these
times were put to trial by a piece of barley bread,
on which the mass had been performed; and if the
accused could not swallow it, they were declared
guilty. This mode of trial was improved by adding
to the <em class="calibre12">bread</em> a slice of <em class="calibre12">cheese</em>; and such was their
credulity and dependance on heaven in these ridiculous
trials, that they were very particular in this
holy <em class="calibre12">bread</em> and <em class="calibre12">cheese</em>, called the <em class="calibre12">corsned</em>. The
bread was to be of unleavened barley, and the
cheese made of ewes milk in the month of May<a id="r5"></a><span class="c12" href="#/Text/Section0044#f5" title="Link target outside of Sample"><sup class="calibre13">[5]</sup></span>.</p>
<p class="c11">The <em class="calibre12">bleeding of a corpse</em> was another proof of
guilt in superstitious ages; nor is the custom yet
entirely abolished. If a person were murdered,
it was believed, that at the touch or approach of
the murderer, the blood gushed out from various
parts of the body. By the side of the bier, if the
smallest change was perceptible in the eyes, mouth,
feet or hands of the corpse, the murderer was conjectured
to be present, and many innocent persons
doubtless must have suffered death from this idle
chimera; for when a body is full of blood, warmed
by a sudden external heat and symptoms of ensuing
putrefaction, some of the blood vessels will
<span id="Page_14" title="14"></span>burst, as they will all in time. This practice was
once allowed in England, and is still looked on in
some of the uncivilized parts of these kingdoms as
a means of detecting the criminal. It forms a rich
picture in the imagination of our old writers; and
their histories and ballads are laboured into pathos
by dwelling on the suppositious phenomenon.</p>
<p class="c11">All these absurd institutions, Robertson observes,
cherished and inculcated, form the superstitions of
the age believing the legendary histories of those
saints who crowd and disgrace the Roman calendar.
These fabulous miracles had been declared authentic
by the bulls of the Popes, and the decrees of
Councils—they were greedily swallowed by the
populace; and whoever believed that the Supreme
Being had interposed miraculously on those trivial
occasions mentioned in legends, could not but expect
his intervention in matters of greater importance
when solemnly referred to his decision. Besides
this ingenious remark, the fact is, that these
customs were a substitute for written laws, which
that barbarous period had not; and as it is impossible
for any society to exist without <em class="calibre12">laws</em>, the ignorance
of the people had recourse to these customs,
which bad and absurd as they were, served to
terminate controversies which might have given
birth to more destructive practices. Ordeals are,
in fact, the rude laws of a barbarous people, who
have not obtained a written code, and not advanced
enough in civilization, to embrace the refined investigations,
the subtle distinctions, and elaborate
inquiries, which are exacted by a Court of Law.</p>
<p class="c11">It may be presumed, that these ordeals owe their
<span id="Page_15" title="15"></span>origin to that one of Moses, called the “Waters
of Jealousy.” The Greeks also had ordeals, for
we read in the Antigonus of Sophocles, that the
soldiers offer to prove their innocence by handling
red hot iron, and walking between fires.</p>
<p class="c11">One cannot but smile at the whimsical ordeals
of the Siamese. Among other practices to discover
the justice of a cause, civil or criminal, they
are particularly attached to the use of certain consecrated
purgative pills, which the contending parties
are made to swallow. He who retains them
longest, gains his cause! The practice of giving
Indians a consecrated grain of rice to swallow, is
known to discover the thief in any company, by
the contortions and dismay evident on the countenance
of the real thief.</p>
<p class="c11">In the middle ages they were acquainted with
secrets to pass unhurt these secret trials: one is
mentioned by Voltaire for undergoing the ordeal
of boiling water; and this statement is confirmed
by some of our late travellers in the East. The
Mevleheh dervises can hold red hot iron between
their teeth. Such artifices have been often publicly
exhibited at Paris and London. On the
ordeal of the Anglo-Saxons, Mr. Sharon Turner
observes, that the hand was not to be immediately
inspected, and was left to the chance of a
good constitution to be so far healed during three
days (the time they required to be bound up and
sealed, before it was examined) as to discover
those appearances when inspected, which were
allowed to be satisfactory. There was also much
preparatory training, suggested by the more experienced:
<span id="Page_16" title="16"></span>besides, the accused had an opportunity
of <em class="calibre12">going alone into the church</em>, and making
<em class="calibre12">terms</em> with the priest. The few spectators were
always at a distance; and cold iron or any other
inoffensive substance might be substituted, and the
fire diminished at the moment. There can be no
doubt they possessed these secrets and medicaments,
which they always took care to have ready
at hand, that they might pass through these trials
in perfect security.</p>
<p class="c11">There is an anecdote of these times given by
Camerarius, in his “Horæ Subscecivæ,” which
may serve to show the readiness of this apparatus.
A rivalship existed between the Austin Friars and
the Jesuits. The Father-general of the Austin
Friars was dining with the Jesuits; and on the
table being removed, he entered into a formal discourse
of the superiority of the monastic order,
and charged the Jesuits, in unqualified terms, with
assuming the title of “Fratres,” while they held
not the three vows, which other monks were obliged
to consider as sacred and binding. The
general of the Austin Friars was very eloquent
and very authoritative: and the superior of the
Jesuits was very unlearned, but not quite half a
fool. He was rather careless about entering the
list of controversy with the Austin Friar, but
arrested his triumph by asking him if he would see
one of his Friars who pretended to be nothing
more than a Jesuit, and one of the Austin Friar’s
who religiously performed the above-mentioned
three vows, show instantly which of them would be
the readiest to obey his superiors? The Austin
<span id="Page_17" title="17"></span>Friar consented. The Jesuit then turning to one
of his brothers, the Holy Friar Mark, who was
waiting on them, said, “Brother Mark, our companions
are cold; I command you, in virtue of the
holy obedience you have sworn to me, to bring here
instantly out of the kitchen fire, and in your hands,
some burning coals, that they may warm themselves
over your hands.” Father Mark instantly
obeys, and to the astonishment of the Austin
Friars, brought in his hands a supply of red burning
coals, and held them to whoever thought proper
to warm himself; and at the command of his
superior, returned them to the kitchen hearth.
The general of the Austin Friars, with the rest of
his brethren, stood amazed; he looked wistfully on
one of his monks, as if he wished to command him
to do the like; but the Austin Monk, who perfectly
understood him, and saw this was not a time
to hesitate, observed,—“Reverend Father, forbear,
and do not command me to tempt God! I
am ready to fetch you fire in a chafing dish, but
not in my bare hands.” The triumph of the Jesuits
was complete; and it is not necessary to add, that
the miracle was noised about, and that the Austin
Friars could never account for it, notwithstanding
their strict performance of the three vows.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0005.vue?27f3869a